import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Loader, Crown, Search, X, Locate, ArrowUp, ArrowDown, Target, Swords, Shield, Flame } from "lucide-react";
import AttackModal from "./AttackModal";

const RANK_TABS = [
  { idx: null, label: "ALL" },
  { idx: 0, label: "GRUNT" },
  { idx: 1, label: "SQ. LEADER" },
  { idx: 2, label: "LIEUTENANT" },
  { idx: 3, label: "WARLORD" },
  { idx: 4, label: "CROWN PR." },
  { idx: 5, label: "KING" },
  { idx: 6, label: "EMPEROR" },
];

/**
 * Weekly horde leaderboard. Two modes:
 *   - readOnly={true}   → public, anonymous. Used on Landing page.
 *   - readOnly={false}  → authed, with claim button. Used on Command page.
 */
export default function LeaderboardPanel({ readOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [rankTab, setRankTab] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const myRowRef = useRef(null);
  const [pendingJump, setPendingJump] = useState(false);
  const [rivalIds, setRivalIds] = useState(new Set());
  const [marking, setMarking] = useState(null);
  const [eligibleMap, setEligibleMap] = useState({}); // user_id -> {shielded, shield_expires_at}
  const [attackTarget, setAttackTarget] = useState(null);

  // Load eligible targets so we know who has ATTACK + who is shielded
  const loadEligible = useCallback(async () => {
    if (readOnly) return;
    try {
      const { data } = await api.get("/attacks/eligible", { params: { limit: 60 } });
      const map = {};
      (data?.targets || []).forEach((t) => {
        map[t.user_id] = {
          shielded: t.shielded,
          expires: t.shield_expires_at,
          revenge_active: t.revenge_active,
        };
      });
      setEligibleMap(map);
    } catch (e) { /* ignore */ }
  }, [readOnly]);

  useEffect(() => { loadEligible(); }, [loadEligible]);

  // Load my rivals so we can highlight + show toggle state
  const loadRivals = useCallback(async () => {
    if (readOnly) return;
    try {
      const { data } = await api.get("/rivals/list");
      setRivalIds(new Set((data?.rivals || []).map((r) => r.user_id)));
    } catch (e) { /* ignore */ }
  }, [readOnly]);

  useEffect(() => { loadRivals(); }, [loadRivals]);

  const toggleRival = async (uid) => {
    if (readOnly || marking) return;
    setMarking(uid);
    try {
      const isRival = rivalIds.has(uid);
      await api.post(`/rivals/${isRival ? "unmark" : "mark"}`, { user_id: uid });
      await loadRivals();
      // Also refresh RivalsPanel via custom event
      window.dispatchEvent(new CustomEvent("rivals-changed"));
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Couldn't update rival.");
    } finally {
      setMarking(null);
    }
  };

  // After expansion (data reload), if a jump is pending, scroll to my row.
  useEffect(() => {
    if (pendingJump && myRowRef.current) {
      myRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setPendingJump(false);
    }
    // also runs after data changes (so post-expand reload triggers scroll)
  }, [pendingJump, data]);

  const jumpToMe = () => {
    if (readOnly || !data?.me) return;
    setSearch("");
    setRankTab(null);    // ensure ALL tab so user is included
    const myInBoard = data.board?.some((r) => r.user_id === data.me.user_id);
    if (myInBoard && myRowRef.current) {
      myRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // Need to expand first; after re-fetch the effect above will scroll
    setExpanded(true);
    setPendingJump(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = readOnly ? "/leaderboard/weekly/public" : "/leaderboard/weekly";
      const params = {};
      if (rankTab !== null) params.rank = rankTab;
      if (expanded) params.limit = 100;
      const { data } = await api.get(path, { params });
      setData(data);
    } catch (e) {
      if (readOnly && e?.response?.status) {
        try {
          const params = {};
          if (rankTab !== null) params.rank = rankTab;
          if (expanded) params.limit = 100;
          const { data } = await api.get("/leaderboard/weekly", { params });
          setData(data);
        } catch {
          // ignore
        }
      }
    } finally {
      setLoading(false);
    }
  }, [readOnly, rankTab, expanded]);

  useEffect(() => { load(); }, [load]);

  // After the rank-change badge has been on screen for 4s, ack the position
  // so the next visit's delta is fresh. Only fires once per delta value.
  useEffect(() => {
    const delta = data?.me?.delta_since_last_seen;
    if (readOnly || !delta || delta === 0) return;
    const t = setTimeout(() => {
      api.post("/leaderboard/ack-position").catch(() => {});
    }, 4000);
    return () => clearTimeout(t);
  }, [readOnly, data?.me?.delta_since_last_seen, data?.me?.position]);

  // Reset expansion when switching tabs
  useEffect(() => { setExpanded(false); }, [rankTab]);

  const claim = async () => {
    setClaiming(true);
    try {
      const { data: res } = await api.post("/leaderboard/claim");
      toast.success(`+${res.reward.horde} HORDE · +${(res.reward.reputation * 100).toFixed(1)}% REP`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="narrative-panel" data-testid="leaderboard-panel-loading">
        <p className="tac-label flex items-center gap-2">
          <Loader className="w-3 h-3 animate-spin" /> WEEKLY HORDE / / LOADING
        </p>
      </div>
    );
  }

  if (!data || !data.board || data.board.length === 0) {
    return (
      <div className="narrative-panel" data-testid="leaderboard-panel-empty">
        <p className="tac-label flex items-center gap-2 mb-4">
          <Trophy className="w-3 h-3 text-blood" /> WEEKLY HORDE / / {data?.week_key || "—"}
        </p>
        <div className="flex gap-1 mb-4 overflow-x-auto pb-1" data-testid="leaderboard-rank-tabs">
          {RANK_TABS.map((t) => (
            <button
              key={t.label}
              onClick={() => setRankTab(t.idx)}
              className={`px-2.5 py-1 text-[0.6rem] font-mono tracking-[0.18em] whitespace-nowrap transition-all border-b ${
                rankTab === t.idx ? "text-bone border-blood" : "text-muted-h border-transparent hover:text-bone"
              }`}
              data-testid={`leaderboard-tab-${t.idx === null ? "all" : t.idx}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-h">
          {rankTab === null
            ? "The leaderboard wakes when the first hordes rise. Be the first."
            : `No ${RANK_TABS.find((r) => r.idx === rankTab)?.label} on the board yet. Be the first.`}
        </p>
      </div>
    );
  }

  const myPos = data.me?.position;
  const eligible = data.eligible_position;
  const reward = data.reward_preview;
  const claimed = data.already_claimed;

  return (
    <div className="narrative-panel" data-testid="leaderboard-panel">
      <div className="flex items-baseline justify-between mb-4">
        <p className="tac-label flex items-center gap-2">
          <Trophy className="w-3 h-3 text-blood" /> WEEKLY HORDE / / {data.week_key}
        </p>
        <span className="font-mono text-[0.65rem] text-muted-h tracking-wider">
          RESETS MONDAY 00:00 UTC
        </span>
      </div>

      {/* Rank-filter tabs */}
      <div className="flex gap-1 mb-2 overflow-x-auto pb-1" data-testid="leaderboard-rank-tabs">
        {RANK_TABS.map((t) => (
          <button
            key={t.label}
            onClick={() => setRankTab(t.idx)}
            className={`px-2.5 py-1 text-[0.6rem] font-mono tracking-[0.18em] whitespace-nowrap transition-all border-b ${
              rankTab === t.idx
                ? "text-bone border-blood"
                : "text-muted-h border-transparent hover:text-bone"
            }`}
            data-testid={`leaderboard-tab-${t.idx === null ? "all" : t.idx}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {data.total_count !== undefined && (
        <p className="text-[0.6rem] font-mono text-muted-h tracking-[0.18em] mb-3"
           data-testid="leaderboard-total-count">
          {data.total_count} {data.total_count === 1 ? "PLAYER" : "PLAYERS"} IN {RANK_TABS.find((r) => r.idx === rankTab)?.label || "ALL"}
        </p>
      )}

      {/* Search + Jump-to-me */}
      {data.board && data.board.length > 5 && (
        <div className="flex items-stretch gap-2 mb-3" data-testid="leaderboard-search-wrap">
          <div className="relative flex-1">
            <Search className="w-3 h-3 text-muted-h absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="SEARCH CALLSIGN..."
              className="w-full pl-7 pr-7 py-1.5 bg-[#0a0303] border border-[var(--border-subtle)] focus:border-blood text-bone font-mono text-xs tracking-wider placeholder:text-muted-h/60 focus:outline-none transition-colors"
              data-testid="leaderboard-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-h hover:text-blood"
                      aria-label="Clear search"
                      data-testid="leaderboard-search-clear">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          {!readOnly && data.me && (
            <button onClick={jumpToMe}
                    className="px-3 border border-[var(--border-subtle)] hover:border-blood hover:text-blood transition-colors text-bone font-mono text-[0.65rem] tracking-widest flex items-center gap-1.5"
                    title="Jump to your row"
                    data-testid="leaderboard-jump-to-me">
              <Locate className="w-3 h-3" /> ME
            </button>
          )}
        </div>
      )}

      <ol className="space-y-1.5" data-testid="leaderboard-list">
        {data.board
          .filter((row) => !search || row.callsign?.toLowerCase().includes(search.toLowerCase()))
          .map((row) => {
            const isMe = !readOnly && data.me && row.user_id === data.me?.user_id;
            const podium = row.position <= 3;
            const isAI = row.is_ai;
            const isRival = !readOnly && !isAI && !isMe && rivalIds.has(row.user_id);
            return (
              <li
                key={row.user_id}
                ref={isMe ? myRowRef : null}
                className={`flex items-center gap-3 px-3 py-2 transition-colors group ${
                  isMe ? "bg-[#1a0606] border-l-2 border-blood" :
                  isRival ? "bg-[#0a0a14] border-l-2 border-[#7FBA9D]/60" :
                  isAI ? "border-l-2 border-[#3a1a1a]" :
                  "border-l-2 border-transparent"
                }`}
                data-testid={`leaderboard-row-${row.position}`}
              >
              <span className={`font-serif-h text-2xl w-8 ${
                row.position === 1 ? "text-blood" :
                podium ? "text-bone" : "text-muted-h"
              }`}>
                {row.position}.
              </span>
              <div className="flex-1 min-w-0">
                <p className={`font-mono text-sm truncate ${
                  isAI ? "text-[#a89c7d] italic" : "text-bone"
                }`} data-testid={`leaderboard-callsign-${row.position}`}>
                  {row.callsign}
                  {isAI && <span className="text-[#5e4a3a] ml-2 text-[0.65rem] not-italic">— LEGEND</span>}
                  {isMe && <span className="text-blood ml-2 text-[0.7rem]">— YOU</span>}
                  {isMe && data.me?.delta_since_last_seen != null && data.me.delta_since_last_seen !== 0 && (
                    <span className={`ml-2 text-[0.65rem] font-mono inline-flex items-center gap-0.5 ${
                      data.me.delta_since_last_seen > 0 ? "text-[#7FBA9D]" : "text-blood"
                    }`}
                    data-testid="rank-change-badge">
                      {data.me.delta_since_last_seen > 0
                        ? <ArrowUp className="w-2.5 h-2.5" />
                        : <ArrowDown className="w-2.5 h-2.5" />}
                      {Math.abs(data.me.delta_since_last_seen)}
                    </span>
                  )}
                </p>
                <p className="font-mono text-[0.65rem] text-muted-h tracking-wider">
                  {row.rank_name?.toUpperCase()}{!isAI && ` · ${row.battles_won} WON`}
                </p>
              </div>
              <span className={`font-serif-h text-xl tabular-nums ${
                isAI ? "text-[#a89c7d]" : "text-bone"
              }`} data-testid={`leaderboard-horde-${row.position}`}>
                {row.horde.toLocaleString()}
              </span>
              {row.position === 1 && <Crown className="w-3 h-3 text-blood" />}
              {!readOnly && !isAI && !isMe && eligibleMap[row.user_id]?.revenge_active && (
                <span title="Revenge token — they attacked you. Stake ×2."
                      className="text-blood inline-flex items-center"
                      data-testid={`leaderboard-revenge-${row.user_id}`}>
                  <Flame className="w-3 h-3" />
                </span>
              )}
              {!readOnly && !isAI && !isMe && eligibleMap[row.user_id]?.shielded && (
                <span title="Defender is shielded for 24h after the last attack."
                      className="text-bone/60 inline-flex items-center"
                      data-testid={`leaderboard-shield-${row.user_id}`}>
                  <Shield className="w-3 h-3" />
                </span>
              )}
              {!readOnly && !isAI && !isMe && eligibleMap[row.user_id] && !eligibleMap[row.user_id].shielded && (
                <button
                  onClick={() => setAttackTarget({
                    user_id: row.user_id,
                    callsign: row.callsign,
                    horde: row.horde,
                    rank_name: row.rank_name,
                    rank_index: row.rank_index,
                    is_rival: isRival,
                    revenge_active: !!eligibleMap[row.user_id]?.revenge_active,
                  })}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 -m-1 text-blood/70 hover:text-blood"
                  title={`Attack ${row.callsign}`}
                  data-testid={`leaderboard-attack-${row.user_id}`}
                >
                  <Swords className="w-3 h-3" />
                </button>
              )}
              {!readOnly && !isAI && !isMe && (
                <button
                  onClick={() => toggleRival(row.user_id)}
                  disabled={marking === row.user_id}
                  className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 -m-1 ${
                    isRival ? "opacity-100 text-[#7FBA9D]" : "text-muted-h hover:text-[#7FBA9D]"
                  }`}
                  title={isRival ? "Unmark as rival" : "Mark as rival"}
                  data-testid={`leaderboard-mark-rival-${row.user_id}`}
                >
                  {marking === row.user_id
                    ? <Loader className="w-3 h-3 animate-spin" />
                    : <Target className="w-3 h-3" fill={isRival ? "currentColor" : "none"} />}
                </button>
              )}
              </li>
            );
          })}
      </ol>

      {/* No-search-results message */}
      {search && data.board.filter((r) => r.callsign?.toLowerCase().includes(search.toLowerCase())).length === 0 && (
        <p className="text-xs text-muted-h text-center py-4 italic"
           data-testid="leaderboard-no-results">
          No callsign matches "{search}".
        </p>
      )}

      {/* Show all / collapse */}
      {!search && data.total_count > data.board.length && !expanded && (
        <button onClick={() => setExpanded(true)}
                className="btn-ghost mt-3 w-full text-[0.65rem]"
                data-testid="leaderboard-show-all">
          SHOW ALL {data.total_count} →
        </button>
      )}
      {!search && expanded && data.board.length > 25 && (
        <button onClick={() => setExpanded(false)}
                className="btn-ghost mt-3 w-full text-[0.65rem]"
                data-testid="leaderboard-collapse">
          ← COLLAPSE
        </button>
      )}

      {!readOnly && data.me && myPos > data.board.length && (
        <div className="mt-3 px-3 py-2 border-t border-[var(--border-subtle)] flex items-center gap-3"
             data-testid="leaderboard-my-row-below">
          <span className="font-serif-h text-2xl w-8 text-muted-h">{myPos}.</span>
          <div className="flex-1 min-w-0">
            <p className="font-mono text-sm text-bone">
              YOU
              {data.me.delta_since_last_seen != null && data.me.delta_since_last_seen !== 0 && (
                <span className={`ml-2 text-[0.65rem] font-mono inline-flex items-center gap-0.5 ${
                  data.me.delta_since_last_seen > 0 ? "text-[#7FBA9D]" : "text-blood"
                }`} data-testid="rank-change-badge-below">
                  {data.me.delta_since_last_seen > 0
                    ? <ArrowUp className="w-2.5 h-2.5" />
                    : <ArrowDown className="w-2.5 h-2.5" />}
                  {Math.abs(data.me.delta_since_last_seen)}
                </span>
              )}
            </p>
            <p className="font-mono text-[0.65rem] text-muted-h tracking-wider">
              {data.me.battles_won} WON
            </p>
          </div>
          <span className="font-serif-h text-xl text-muted-h tabular-nums">
            {data.me.horde.toLocaleString()}
          </span>
        </div>
      )}

      {!readOnly && eligible && reward && (
        <div className="mt-5 border-l-2 border-blood pl-4 py-2" data-testid="leaderboard-reward-panel">
          <p className="tac-label text-blood">
            PODIUM // POSITION {eligible}
          </p>
          <p className="text-sm text-bone mt-2 leading-relaxed">
            +<span className="font-mono">{reward.horde}</span> zombies to your personal horde · +<span className="font-mono">{(reward.reputation * 100).toFixed(1)}%</span> reputation
          </p>
          <p className="text-[0.65rem] text-muted-h mt-1 leading-relaxed">
            Scaled by your rank tier and the size of this week's top horde ({data.top_horde.toLocaleString()}).
          </p>
          <button
            onClick={claim}
            disabled={claiming || claimed}
            className="btn-blood mt-3 w-full disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="leaderboard-claim-button"
          >
            {claiming ? <Loader className="w-3 h-3 animate-spin inline" /> :
             claimed ? "CLAIMED — RETURN MONDAY" :
             `CLAIM // +${reward.horde} HORDE`}
          </button>
        </div>
      )}

      {!readOnly && data.me && !eligible && (
        <p className="text-[0.7rem] text-muted-h mt-4 leading-relaxed italic" data-testid="leaderboard-not-podium">
          Climb to the top three to claim a weekly reward. Reward scales with the leader's horde and your rank.
        </p>
      )}

      {readOnly && (
        <p className="text-[0.7rem] text-muted-h mt-4 leading-relaxed italic">
          Top three each week receive horde + reputation bonuses scaled to their rank and the leader's size.
        </p>
      )}

      {attackTarget && (
        <AttackModal
          target={attackTarget}
          onClose={async () => {
            setAttackTarget(null);
            // Refresh leaderboard + eligibility ONCE the modal is dismissed,
            // so the modal isn't unmounted mid-result by a loading state flip.
            await Promise.all([load(), loadEligible()]);
            window.dispatchEvent(new CustomEvent("game-state-changed"));
          }}
          onResolved={() => {
            // Lightweight: just eligibility (no `loading` toggle) so the modal
            // can render its result panel without being unmounted.
            loadEligible();
          }}
        />
      )}
    </div>
  );
}
